import {RefractoryPlacingMethod} from '@domain/refractory-product-catalog'
import {Button, ControlledAutocompleteSelect, GenericInput, Loading} from '@hm/fundamental'
import {mdiFilterOutline, mdiRefresh} from '@mdi/js'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useRefractoryCatalogFilterOptionsQuery} from '../../hooks'

export interface RefractoryCatalogFilters {
  name: string
  manufacturerIds: string[]
  classificationIds: string[]
  classificationGroupIds: string[]
  bondingIds: string[]
  placingMethods: RefractoryPlacingMethod[]
}

export const EMPTY_REFRACTORY_CATALOG_FILTERS: RefractoryCatalogFilters = {
  name: '',
  manufacturerIds: [],
  classificationIds: [],
  classificationGroupIds: [],
  bondingIds: [],
  placingMethods: []
}

interface RefractoryMaterialsFiltersProps {
  appliedFilters: RefractoryCatalogFilters
  onApply: (filters: RefractoryCatalogFilters) => void
  onReset: () => void
}

const placingMethodValues = new Set<string>(Object.values(RefractoryPlacingMethod))

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getOptionLabel = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (!isRecord(value)) return undefined

  for (const key of ['label', 'name', 'classification', 'description', 'enumValue', 'value']) {
    const candidate = value[key]
    if (typeof candidate === 'string') return candidate
  }

  for (const candidate of Object.values(value)) {
    const label = getOptionLabel(candidate)
    if (label) return label
  }

  return undefined
}

const hasFilters = (filters: RefractoryCatalogFilters): boolean =>
  Boolean(filters.name) ||
  filters.manufacturerIds.length > 0 ||
  filters.classificationIds.length > 0 ||
  filters.classificationGroupIds.length > 0 ||
  filters.bondingIds.length > 0 ||
  filters.placingMethods.length > 0

export const RefractoryMaterialsFilters: React.FC<RefractoryMaterialsFiltersProps> = ({
  appliedFilters,
  onApply,
  onReset
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [draftFilters, setDraftFilters] = useState(appliedFilters)
  const [filterInputKey, setFilterInputKey] = useState(0)
  const {data: filterOptions, isFetching} = useRefractoryCatalogFilterOptionsQuery()

  const filterOptionsLoading = isFetching ? (
    <div
      className="absolute left-0 right-0 z-10 mt-1 flex items-center justify-center rounded-lg bg-container_background p-4 shadow-lg"
      role="status"
      aria-label={t('refraCatalog.loading')}
      data-test-id="refra_catalog_filter_options_loading"
    >
      <Loading size="small" />
    </div>
  ) : undefined

  useEffect(() => {
    setDraftFilters(appliedFilters)
    setFilterInputKey((current) => current + 1)
  }, [appliedFilters])

  const hasActiveFilters = hasFilters(draftFilters) || hasFilters(appliedFilters)

  const setPlacingMethods = (values: (string | number)[]) => {
    setDraftFilters((current) => ({
      ...current,
      placingMethods: values
        .map(String)
        .filter((value): value is RefractoryPlacingMethod => placingMethodValues.has(value))
    }))
  }

  const handleReset = () => {
    setDraftFilters(EMPTY_REFRACTORY_CATALOG_FILTERS)
    setFilterInputKey((current) => current + 1)
    onReset()
  }

  const manufacturerOptions = (filterOptions?.manufacturers ?? []).flatMap(({id, name}) => {
    const label = getOptionLabel(name)
    return label ? [{label, value: String(id)}] : []
  })
  const classificationOptions = (filterOptions?.classifications ?? []).flatMap(
    ({id, classification}) => {
      const label = getOptionLabel(classification)
      return label ? [{label, value: String(id)}] : []
    }
  )
  const classificationGroupOptions = (filterOptions?.classificationGroups ?? []).flatMap(
    ({id, name}) => {
      const label = getOptionLabel(name)
      return label ? [{label, value: String(id)}] : []
    }
  )
  const bondingOptions = (filterOptions?.bondings ?? []).flatMap(({id, name}) => {
    const label = getOptionLabel(name)
    return label ? [{label, value: String(id)}] : []
  })
  const placingMethodOptions = Object.values(RefractoryPlacingMethod).map((value) => ({
    label: value,
    value
  }))

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
        <GenericInput
          name="refra_catalog_filter"
          key={filterInputKey}
          defaultValue={draftFilters.name}
          placeholder={t('refraCatalog.filters.name')}
          onChange={(name) => setDraftFilters((current) => ({...current, name}))}
          data-cy="refra-catalog-filter"
        />
        <ControlledAutocompleteSelect
          label={t('refraCatalog.filters.supplier')}
          options={manufacturerOptions}
          customSelectionComponent={filterOptionsLoading}
          selectedValues={draftFilters.manufacturerIds}
          onChange={(values) =>
            setDraftFilters((current) => ({...current, manufacturerIds: values.map(String)}))
          }
          isMultiSelect
          data-cy="refra-catalog-supplier-filter"
        />
        <ControlledAutocompleteSelect
          label={t('refraCatalog.filters.classification')}
          options={classificationOptions}
          customSelectionComponent={filterOptionsLoading}
          selectedValues={draftFilters.classificationIds}
          onChange={(values) =>
            setDraftFilters((current) => ({...current, classificationIds: values.map(String)}))
          }
          isMultiSelect
          data-cy="refra-catalog-classification-filter"
        />
        <ControlledAutocompleteSelect
          label={t('refraCatalog.filters.classificationGroup')}
          options={classificationGroupOptions}
          customSelectionComponent={filterOptionsLoading}
          selectedValues={draftFilters.classificationGroupIds}
          onChange={(values) =>
            setDraftFilters((current) => ({
              ...current,
              classificationGroupIds: values.map(String)
            }))
          }
          isMultiSelect
          data-cy="refra-catalog-classification-group-filter"
        />
        <ControlledAutocompleteSelect
          label={t('refraCatalog.filters.bonding')}
          options={bondingOptions}
          customSelectionComponent={filterOptionsLoading}
          selectedValues={draftFilters.bondingIds}
          onChange={(values) =>
            setDraftFilters((current) => ({...current, bondingIds: values.map(String)}))
          }
          isMultiSelect
          data-cy="refra-catalog-bonding-filter"
        />
        <ControlledAutocompleteSelect
          label={t('refraCatalog.filters.placingMethod')}
          options={placingMethodOptions}
          selectedValues={draftFilters.placingMethods}
          onChange={setPlacingMethods}
          isMultiSelect
          data-cy="refra-catalog-placing-method-filter"
        />
      </div>
      <div className="flex shrink-0 items-end justify-end gap-2 lg:h-[112px]">
        <Button
          variant="tertiary"
          icon={mdiRefresh}
          onClick={handleReset}
          disabled={!hasActiveFilters}
          data-cy="refra-catalog-reset-filters"
        >
          {t('refraCatalog.filters.reset')}
        </Button>
        <Button
          variant="tertiary"
          icon={mdiFilterOutline}
          onClick={() => onApply(draftFilters)}
          data-cy="refra-catalog-apply-filters"
        >
          {t('refraCatalog.filters.apply')}
        </Button>
      </div>
    </div>
  )
}
